import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Box, IconButton } from '@mui/material';
import { MdClose, MdChevronLeft, MdChevronRight } from 'react-icons/md'; // your react-icons package
import { useSwipeable } from 'react-swipeable';



const promoConfig = {
  "showModal": true,
  "delaySeconds": 4,
  "promoImages": [
    { "id": 1, "url": "images/PromoCarouselPopUp/DVG-MNG.jpeg", "alt": "Summer Sale 50% Off" },
    { "id": 2, "url": "images/PromoCarouselPopUp/KLBDM.jpeg", "alt": "Free Shipping Weekend" },
    { "id": 3, "url": "images/PromoCarouselPopUp/MantralayaSBC.jpeg", "alt": "New Arrivals" },
    { "id": 4, "url": "images/PromoCarouselPopUp/SIN-MMB.jpeg", "alt": "New Arrivals" },
    { "id": 5, "url": "images/PromoCarouselPopUp/TPT-MMB.jpeg", "alt": "New Arrivals" }
   
  ]
}


export default function PromoModal(promoConfigProp) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const images = promoConfigProp?.promoImages || promoConfig.promoImages || [];

  // Helper navigation logic
  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  // Setup delayed trigger and lifecycle
  useEffect(() => {
    if (!promoConfig.showModal || images.length === 0) return;


     const navEntries = performance.getEntriesByType('navigation');
     const isHardReload = navEntries.length > 0 && navEntries[0].type === 'reload';

     const hasNavigatedThisSession = sessionStorage.getItem('promo_widget_active');

     if (hasNavigatedThisSession && !isHardReload) {
      return; 
     }

     sessionStorage.setItem('promo_widget_active', 'true');

    // const hasSeenModal = sessionStorage.getItem('hasSeenPromoModal');
    // if (hasSeenModal) return;

    const timer = setTimeout(() => {
      setOpen(true);
    }, (promoConfig.delaySeconds || 5) * 1000);

    return () => clearTimeout(timer);
  }, [images.length]);

  // Autoplay functionality
  useEffect(() => {
    if (!open) return;
    const autoplayTimer = setInterval(handleNext, 4000);
    return () => clearInterval(autoplayTimer);
  }, [open, handleNext]);

  const handleClose = () => {
    setOpen(false);
    // sessionStorage.setItem('hasSeenPromoModal', 'true');
  };

  // Configure swipe gestures for touch devices
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handlePrev(),
    preventScrollOnSwipe: true,
    trackMouse: true
  });

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      sx={styles.modalBackdrop}
    >
      <Box sx={styles.modalContainer}>
        {/* Close Action */}
        <IconButton onClick={handleClose} sx={styles.closeBtn} aria-label="close">
          <MdClose size={24} />
        </IconButton>

        {/* Carousel Window */}
        <Box {...swipeHandlers} sx={styles.carouselWrapper}>
          <Box 
            sx={{
              ...styles.sliderTrack,
              transform: `translateX(-${activeIndex * 100}%)`,
            }}
          >
            {images.map((img) => (
              <Box key={img.id} sx={styles.slide}>
                <Box
                  component="img"
                  src={img.url}
                  alt={img.alt}
                  sx={styles.slideImage}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Manual Arrow Controls */}
        <IconButton onClick={handlePrev} sx={{ ...styles.arrowBtn, left: 8 }}>
          <MdChevronLeft size={28} />
        </IconButton>
        <IconButton onClick={handleNext} sx={{ ...styles.arrowBtn, right: 8 }}>
          <MdChevronRight size={28} />
        </IconButton>

        {/* Pagination Dots */}
        <Box sx={styles.dotContainer}>
          {images.map((_, index) => (
            <Box
              key={index}
              onClick={() => setActiveIndex(index)}
              sx={{
                ...styles.dot,
                backgroundColor: activeIndex === index ? '#fff' : 'rgba(255,255,255,0.4)',
                transform: activeIndex === index ? 'scale(1.2)' : 'scale(1)',
              }}
            />
          ))}
        </Box>
      </Box>
    </Modal>
  );
}

// Custom Sx MUI Stylesheet
const styles = {
  modalBackdrop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(4px)',
  },
  modalContainer: {
    position: 'relative',
    width: '90%',
    maxWidth: '550px',
    backgroundColor: 'background.paper',
    borderRadius: '12px',
    boxShadow: 24,
    overflow: 'hidden',
    outline: 'none',
  },
  closeBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
    color: '#000',
  },
  carouselWrapper: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    cursor: 'grab',
    '&:active': { cursor: 'grabbing' }
  },
  sliderTrack: {
    display: 'flex',
    width: '100%',
    transition: 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
  },
  slide: {
    minWidth: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  slideImage: {
    width: '100%',
    height: 'auto',
    maxHeight: '400px',
    objectFit: 'cover',
    display: 'block',
  },
  arrowBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 5,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    color: '#fff',
    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
  },
  dotContainer: {
    position: 'absolute',
    bottom: 16,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '8px',
    zIndex: 5,
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  }
};
